import { Component, DestroyRef, ElementRef, HostListener, NgZone, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject, debounceTime, forkJoin } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { MainLayoutComponent } from '../../../_layouts/main-layout/main-layout.component';
import { AlertService } from '../../../_services/alert.service';
import { UserService } from '../../../_services/users.service';
import { ConfirmDialogComponent } from '../../../../Shared/_components/confirm-dialog/confirm-dialog.component';
import { readShipmentsSheet, writeShipmentsSheet } from './shipment-sheet';
import { ShipmentImportPreviewComponent } from './shipment-import-preview.component';
import { findState } from './container-command';
import { ContainersListComponent } from './containers-list.component';
import { Listening, listenOnce, speechSupported } from '../../../../Shared/_services/speech';
import { PurchaseShipmentService, ShipmentSummary } from '../../../_services/purchase-shipment.service';
import { PurchaseShipment } from '../../../_interfaces/purchase-shipment';
import { SHIPMENT_STATUSES, shipmentStatusClass, shipmentStatusDot, shipmentStatusText } from './shipment-status';
import { currencySymbol } from '../currencies';
import { SHIPMENTS_LIST_KEY, saveListQuery } from '../list-state';
import { PaginatorPagesDirective } from '../../../../Shared/_directives/paginator-pages.directive';

@Component({
    selector: 'app-shipments',
    imports: [PaginatorPagesDirective,
        MainLayoutComponent,
        MatIconModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        MatPaginatorModule,
        MatMenuModule,
        DatePipe,
        FormsModule,
        CommonModule,
        RouterLink,
        ContainersListComponent,
    ],
    templateUrl: './shipments.component.html',
    // Paginator / row styles shared with the purchase order list.
    styleUrls: ['../purchases.component.css', './shipments.component.css']
})
export class ShipmentsComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private search$ = new Subject<void>();

  shipments: PurchaseShipment[] = [];
  loading = true;
  loadError = false;
  count = 0;
  pageNumber = 0;
  itemsPerPage = 10;

  statusTabs = ['ALL', ...SHIPMENT_STATUSES];
  status = 'ALL';
  search = '';
  lateOnly = false;

  statusCounts: Record<string, number> = {};
  summary: ShipmentSummary = { inTransit: 0, late: 0, arrivingSoon: 0, awaitingClearance: 0 };

  getStatusClass = shipmentStatusClass;
  currencySymbol = currencySymbol;
  readonly skeletonRows = [1, 2, 3, 4, 5];

  @ViewChild('searchBox') searchBox?: ElementRef<HTMLInputElement>;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private alertService: AlertService,
    private shipmentService: PurchaseShipmentService,
    private userService: UserService,
    private dialog: MatDialog,
  ) {}

  // ---- Selection (kept across pages and filters) ----
  readonly selected = new Map<number, PurchaseShipment>();
  bulkBusy = false;
  readonly bulkStates = [
    { value: 'BOOKED', label: 'Booked' },
    { value: 'IN TRANSIT', label: 'In transit' },
    { value: 'ARRIVED', label: 'Arrived' },
    { value: 'CLEARED', label: 'Cleared' },
    { value: 'DELIVERED', label: 'Delivered' },
    { value: 'CANCELLED', label: 'Cancelled' },
  ];

  isSelected(s: PurchaseShipment): boolean {
    return this.selected.has(s.id);
  }

  toggleSelect(s: PurchaseShipment, event?: Event): void {
    event?.stopPropagation();
    if (this.selected.has(s.id)) this.selected.delete(s.id);
    else this.selected.set(s.id, s);
  }

  get allOnPageSelected(): boolean {
    return this.shipments.length > 0 && this.shipments.every((s) => this.selected.has(s.id));
  }

  get someOnPageSelected(): boolean {
    return this.shipments.some((s) => this.selected.has(s.id)) && !this.allOnPageSelected;
  }

  toggleSelectPage(): void {
    if (this.allOnPageSelected) this.shipments.forEach((s) => this.selected.delete(s.id));
    else this.shipments.forEach((s) => this.selected.set(s.id, s));
  }

  clearSelection(): void {
    this.selected.clear();
  }

  exportSelected(): void {
    const ids = [...this.selected.keys()];
    this.bulkBusy = true;
    this.shipmentService.exportShipments({ ids }).subscribe({
      next: (shipments) => {
        this.bulkBusy = false;
        writeShipmentsSheet(shipments, `shipments-selected-${new Date().toISOString().slice(0, 10)}.xlsx`);
        this.alertService.alert(`${shipments.length} shipment${shipments.length === 1 ? '' : 's'} exported`, 'success');
      },
      error: (error) => {
        this.bulkBusy = false;
        this.alertService.alert(error?.error?.message || 'Export failed', 'error');
      },
    });
  }

  bulkSetState(state: { value: string; label: string }): void {
    const n = this.selected.size;
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: `Set ${n} shipment${n === 1 ? '' : 's'} to ${state.label}?`,
          message: state.value === 'CANCELLED'
            ? 'They stop counting as stock on the way. Their containers keep their own states.'
            : 'Their containers move with them - except any already received, which stay as they are.',
          detail: 'Shipments whose containers are all received are skipped.',
          confirmText: `Set to ${state.label}`,
          tone: state.value === 'CANCELLED' ? 'danger' : 'info',
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result?.confirmation) return;
        this.bulkBusy = true;
        this.shipmentService.bulkSetStatus([...this.selected.keys()], state.value).subscribe({
          next: (r) => {
            this.bulkBusy = false;
            this.alertService.alert(
              `${r.updated} shipment${r.updated === 1 ? '' : 's'} set to ${state.label}` + (r.skipped ? ` - ${r.skipped} skipped (already received)` : ''),
              'success',
            );
            this.selected.clear();
            this.load();
          },
          error: (error) => {
            this.bulkBusy = false;
            this.alertService.alert(error?.error?.message || 'Could not update the shipments', 'error');
          },
        });
      });
  }

  bulkDelete(): void {
    const list = [...this.selected.values()];
    const n = list.length;
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: `Delete ${n} shipment${n === 1 ? '' : 's'}?`,
          message: `${list.slice(0, 6).map((s) => s.reference).join(', ')}${n > 6 ? ` and ${n - 6} more` : ''} - with their lines and containers. This can't be undone.`,
          detail: 'The purchase orders and their stock are not affected.',
          confirmText: `Delete ${n} shipment${n === 1 ? '' : 's'}`,
          tone: 'danger',
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result?.confirmation) return;
        this.bulkBusy = true;
        forkJoin(list.map((s) => this.shipmentService.deleteShipment(s.id))).subscribe({
          next: () => {
            this.bulkBusy = false;
            this.alertService.alert(`${n} shipment${n === 1 ? '' : 's'} deleted`, 'success');
            this.selected.clear();
            this.pageNumber = 0;
            this.load();
          },
          error: (error) => {
            this.bulkBusy = false;
            this.alertService.alert(error?.error?.message || 'Some shipments could not be deleted', 'error');
            this.selected.clear();
            this.load();
          },
        });
      });
  }

  // ---- One container's state: said into its mic, or picked from its menu ----
  private zone = inject(NgZone);
  listeningContainerId: number | null = null;
  savingContainerId: number | null = null;
  private listening: Listening | null = null;
  readonly containerStateOptions = [
    { value: 'BOOKED', label: 'Booked' },
    { value: 'IN TRANSIT', label: 'In transit' },
    { value: 'ARRIVED', label: 'Arrived' },
    { value: 'CLEARED', label: 'Cleared' },
    { value: 'DELIVERED', label: 'Delivered' },
  ];

  // Click the mic, say "cleared" / "at the yard" / "at sea" - that container updates.
  speakContainerState(c: { id: number; containerNo: string | null; status?: string | null }, s: PurchaseShipment): void {
    if (this.listeningContainerId === c.id) {
      this.listening?.stop();
      return;
    }
    if (!speechSupported()) {
      this.alertService.alert("This browser can't listen (Brave blocks it) - pick the state from its badge instead", 'error');
      return;
    }
    this.listening?.stop();
    this.listeningContainerId = c.id;
    this.listening = listenOnce(this.zone);
    this.listening.result
      .then((heard) => {
        this.listeningContainerId = null;
        const state = findState(heard);
        const said = heard.split(' | ')[0];
        if (!state) {
          this.alertService.alert(`Heard “${said}” - say a state, e.g. cleared, delivered, at sea`, 'error');
          return;
        }
        if (state.value === 'RECEIVED') {
          this.alertService.alert('Received is set by checking the container in at Incoming Stocks', 'error');
          return;
        }
        if (state.value === this.containerState(c, s)) {
          this.alertService.alert(`${c.containerNo || 'It'} is already ${state.label.toLowerCase()}`, 'success');
          return;
        }
        this.setContainerState(c, s, state.value);
      })
      .catch((message: string) => {
        this.listeningContainerId = null;
        this.alertService.alert(message, 'error');
      });
  }

  setContainerState(c: { id: number; containerNo: string | null }, s: PurchaseShipment, value: string): void {
    const label = this.containerStateOptions.find((o) => o.value === value)?.label ?? value;
    this.savingContainerId = c.id;
    this.shipmentService.setContainerStatus([c.id], value).subscribe({
      next: () => {
        this.savingContainerId = null;
        this.alertService.alert(`${c.containerNo || 'Container'} set to ${label}`, 'success');
        this.load();
      },
      error: (error) => {
        this.savingContainerId = null;
        this.alertService.alert(error?.error?.message || 'Could not update the container', 'error');
      },
    });
  }

  // ---- Spreadsheet export / import ----
  exporting = false;
  importing = false;

  // Everything matching the current search / tab / late filter.
  exportSheet(): void {
    this.exporting = true;
    this.shipmentService
      .exportShipments({ search: this.search.trim() || undefined, status: this.status, lateOnly: this.lateOnly || undefined })
      .subscribe({
        next: (shipments) => {
          this.exporting = false;
          if (!shipments.length) {
            this.alertService.alert('No shipments to export with these filters', 'error');
            return;
          }
          const stamp = new Date().toISOString().slice(0, 10);
          writeShipmentsSheet(shipments, `shipments-${stamp}.xlsx`);
          this.alertService.alert(`${shipments.length} shipment${shipments.length === 1 ? '' : 's'} exported`, 'success');
        },
        error: (error) => {
          this.exporting = false;
          this.alertService.alert(error?.error?.message || 'Export failed', 'error');
        },
      });
  }

  // Reads the file, has the server check it, then shows what would change.
  async onImportFile(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = ''; // lets the same file be picked again after fixing it
    if (!file) return;
    this.importing = true;
    try {
      const parsed = await readShipmentsSheet(file);
      if (!parsed.shipments.length) {
        this.importing = false;
        this.alertService.alert('No shipment rows found in that file', 'error');
        return;
      }
      this.shipmentService.importShipments({ dryRun: true, shipments: parsed.shipments }).subscribe({
        next: (check) => {
          this.importing = false;
          this.dialog
            .open(ShipmentImportPreviewComponent, {
              width: '760px',
              maxWidth: '96vw',
              data: { fileName: file.name, shipments: parsed.shipments, results: check.results },
            })
            .afterClosed()
            .subscribe((applied) => {
              if (applied) this.load();
            });
        },
        error: (error) => {
          this.importing = false;
          this.alertService.alert(error?.error?.message || 'Could not check that file', 'error');
        },
      });
    } catch {
      this.importing = false;
      this.alertService.alert("Couldn't read that file - use an .xlsx exported from here", 'error');
    }
  }

  // Deleting is for super admins only (the backend checks too).
  readonly canDelete = (this.userService.isLoggedIn().userrole ?? '')
    .split(',')
    .map((r: string) => r.trim())
    .includes('S-ADMIN');

  confirmDelete(s: PurchaseShipment): void {
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: `Delete ${s.reference}?`,
          message: `This removes the shipment with its ${s.itemCount ?? 0} line${s.itemCount === 1 ? '' : 's'} and containers. It can't be undone.`,
          detail: 'The purchase orders and their stock are not affected.',
          confirmText: 'Delete shipment',
          tone: 'danger',
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result?.confirmation) return;
        this.shipmentService.deleteShipment(s.id).subscribe({
          next: () => {
            this.alertService.alert(`${s.reference} deleted`, 'success');
            // Step back a page if that was the last row on it.
            if (this.shipments.length === 1 && this.pageNumber > 0) this.pageNumber--;
            this.load();
          },
          error: (error) => this.alertService.alert(error?.error?.message || 'Could not delete shipment', 'error'),
        });
      });
  }

  ngOnInit(): void {
    // Search as you type; Enter searches straight away.
    this.search$.pipe(debounceTime(350), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.applyFilters());
    this.readUrl();
    this.load();
  }

  // Filters and page live in the URL (and are remembered for the shipment
  // page's back/save), so returning lands on the same view.
  // Shipments (default), or every container on its own row.
  view: 'shipments' | 'containers' = 'shipments';

  setView(view: 'shipments' | 'containers'): void {
    if (this.view === view) return;
    this.view = view;
    this.writeUrl();
  }

  private readUrl(): void {
    const q = this.route.snapshot.queryParamMap;
    if (q.get('view') === 'containers') this.view = 'containers';
    this.search = q.get('q') ?? '';
    const status = q.get('status');
    if (status && this.statusTabs.includes(status)) this.status = status;
    this.lateOnly = q.get('late') === '1';
    const page = Number(q.get('page'));
    if (page > 1) this.pageNumber = page - 1;
    const size = Number(q.get('size'));
    if ([10, 20, 50].includes(size)) this.itemsPerPage = size;
  }

  private writeUrl(): void {
    const queryParams = {
      q: this.search.trim() || null,
      status: this.status !== 'ALL' ? this.status : null,
      late: this.lateOnly ? '1' : null,
      page: this.pageNumber > 0 ? this.pageNumber + 1 : null,
      size: this.itemsPerPage !== 10 ? this.itemsPerPage : null,
      view: this.view === 'containers' ? 'containers' : null,
    };
    saveListQuery(queryParams, SHIPMENTS_LIST_KEY);
    this.router.navigate([], { relativeTo: this.route, replaceUrl: true, queryParams });
  }

  // "/" jumps to the search box.
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key !== '/' || event.metaKey || event.ctrlKey) return;
    const target = event.target as HTMLElement | null;
    if (target && (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable)) return;
    event.preventDefault();
    this.searchBox?.nativeElement.focus();
  }

  load(): void {
    this.writeUrl();
    this.loading = true;
    this.loadError = false;
    this.shipmentService
      .getShipments({
        pageIndex: this.pageNumber,
        pageSize: this.itemsPerPage,
        search: this.search.trim() || undefined,
        status: this.status,
        lateOnly: this.lateOnly || undefined,
      })
      .subscribe({
        next: (response) => {
          this.shipments = response.items;
          this.count = response.count;
          this.statusCounts = response.statusCounts ?? {};
          this.summary = response.summary ?? this.summary;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.loadError = true;
          this.alertService.alert('Could not load shipments', 'error');
        },
      });
  }

  onSearchInput(): void {
    this.search$.next();
  }

  clearSearch(): void {
    this.search = '';
    this.applyFilters();
  }

  applyFilters(): void {
    this.pageNumber = 0;
    this.load();
  }

  setStatus(status: string): void {
    this.status = status;
    // Late only means still on its way, so a tab that can't be late clears it.
    if (this.lateOnly && !['ALL', 'BOOKED', 'IN TRANSIT'].includes(status)) this.lateOnly = false;
    this.applyFilters();
  }

  showLate(): void {
    this.status = 'ALL';
    this.search = '';
    this.lateOnly = true;
    this.applyFilters();
  }

  showAwaitingClearance(): void {
    this.status = 'ARRIVED';
    this.search = '';
    this.lateOnly = false;
    this.applyFilters();
  }

  showInTransit(): void {
    this.status = 'IN TRANSIT';
    this.search = '';
    this.lateOnly = false;
    this.applyFilters();
  }

  onResetFilters(): void {
    this.status = 'ALL';
    this.search = '';
    this.lateOnly = false;
    this.applyFilters();
  }

  get filtersActive(): boolean {
    return !!this.search.trim() || this.status !== 'ALL' || this.lateOnly;
  }

  tabCount(tab: string): number {
    if (tab === 'ALL') return Object.values(this.statusCounts).reduce((a, b) => a + b, 0);
    return this.statusCounts[tab] ?? 0;
  }

  updateList(event: PageEvent): void {
    if (this.itemsPerPage !== event.pageSize) {
      this.itemsPerPage = event.pageSize || 10;
      this.pageNumber = 0;
    } else {
      this.pageNumber = event.pageIndex;
    }
    this.load();
  }

  // Whole days from today to the ETA (negative = overdue). Only while the
  // shipment is still on its way to port.
  daysToEta(s: PurchaseShipment): number | null {
    if (!s.eta || !['BOOKED', 'IN TRANSIT'].includes(s.status)) return null;
    const eta = new Date(s.eta);
    const today = new Date();
    return Math.round(
      (Date.UTC(eta.getFullYear(), eta.getMonth(), eta.getDate()) -
        Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) / 86_400_000,
    );
  }

  // How far through its sailing a shipment is (ETD -> ETA), for the bar.
  journeyPct(s: PurchaseShipment): number | null {
    if (!s.etd || !s.eta) return null;
    if (['ARRIVED', 'CLEARED', 'DELIVERED', 'RECEIVED'].includes(s.status)) return 100;
    if (s.status === 'CANCELLED') return null;
    const start = new Date(s.etd).getTime();
    const end = new Date(s.eta).getTime();
    if (end <= start) return null;
    return Math.max(0, Math.min(100, Math.round(((Date.now() - start) / (end - start)) * 100)));
  }

  // Dot / accent colour per state, matching the badges in shipment-status.
  statusDot(status: string): string {
    return shipmentStatusDot(status);
  }

  // The shipment's container numbers (saved as one comma-joined summary).
  containerList(s: PurchaseShipment): string[] {
    return (s.containerNo || '').split(',').map((c) => c.trim()).filter(Boolean);
  }

  // Shipments whose container breakdown is open.
  expanded = new Set<number>();

  toggleExpand(id: number, event: Event): void {
    event.stopPropagation();
    if (this.expanded.has(id)) this.expanded.delete(id);
    else this.expanded.add(id);
  }

  // A container without its own state follows the shipment's.
  containerState(c: { status?: string | null }, s: PurchaseShipment): string {
    return c.status || s.status;
  }

  containerUnits(c: { lines?: { quantity: number }[] }): number {
    return (c.lines ?? []).reduce((n, l) => n + (Number(l.quantity) || 0), 0);
  }

  shipmentUnits(s: PurchaseShipment): number {
    return (s.containers ?? []).reduce((n, c) => n + this.containerUnits(c), 0);
  }

  // One line under the container bar: the most useful thing to know.
  containerSummary(s: PurchaseShipment): { text: string; tone: string } {
    const list = s.containers ?? [];
    const n = list.length;
    const count = (state: string) => list.filter((c) => this.containerState(c, s) === state).length;
    const received = count('RECEIVED');
    const delivered = count('DELIVERED');
    const cleared = this.clearedCount(s);
    if (received === n) return { text: 'All received', tone: shipmentStatusText('RECEIVED') };
    if (received) return { text: `${received}/${n} received`, tone: shipmentStatusText('RECEIVED') };
    if (delivered) return { text: `${delivered}/${n} at the yard`, tone: shipmentStatusText('DELIVERED') };
    if (cleared) return { text: `${cleared}/${n} cleared`, tone: shipmentStatusText('CLEARED') };
    const atSea = count('IN TRANSIT');
    if (atSea) return { text: `${atSea}/${n} at sea`, tone: shipmentStatusText('IN TRANSIT') };
    const arrived = count('ARRIVED');
    if (arrived) return { text: `${arrived}/${n} at port`, tone: shipmentStatusText('ARRIVED') };
    return { text: 'Booked', tone: shipmentStatusText('BOOKED') };
  }

  clearedCount(s: PurchaseShipment): number {
    return (s.containers ?? []).filter((c) => ['CLEARED', 'DELIVERED', 'RECEIVED'].includes(this.containerState(c, s))).length;
  }

  // "SKU x qty" per line, for the "+N more" tooltip.
  skuList(lines: { sku: string | null; productName: string; quantity: number }[]): string {
    return lines.map((l) => `${l.sku || l.productName} ×${l.quantity}`).join('\n');
  }

  atPort(s: PurchaseShipment): boolean {
    return ['ARRIVED', 'CLEARED', 'DELIVERED', 'RECEIVED'].includes(s.status);
  }

  onCreate(): void {
    this.router.navigate(['/shipments/create']);
  }

  onOpen(shipment: PurchaseShipment): void {
    this.router.navigate(['/shipments/edit', shipment.id]);
  }

  openContainer(id: number): void {
    this.router.navigate(['/shipments/container', id]);
  }

  onOpenOrder(orderId: number, event?: Event): void {
    event?.stopPropagation();
    this.router.navigate(['/purchases/edit', orderId]);
  }
}
